import React, { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Calendar, RefreshCcw, Building2, Download, } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import useSettlement from "../../Services/OrderServices/SettlementServices/useSettlement";

const NA = "N/A";

const toDateStr = (d) => {
    try {
        return new Date(d).toISOString().split("T")[0];
    } catch {
        return "";
    }
};

const safe = (v, fb = NA) => {
    if (v === null || v === undefined || String(v).trim() === "") return fb;
    return String(v);
};

const fmtMoney = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return safe(v);
    return n.toFixed(2);
};

export default function RestaurantWiseTransactions() {

    const {getRestaurantWiseTransactions, updateSettlementTransaction}=useSettlement();

    const { api } = useContext(GlobalContext);

    const params = useParams();
    const userid = params.clientId || params.userid || params.id;
    const name = decodeURIComponent(params.clientName || "") || userid || "Client";

    const today = useMemo(() => toDateStr(new Date()), []);
    const [date, setDate] = useState(today);

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);


    const fetchRestaurantWise = async () => {
  if (!userid) {
    toast.error("User id missing in route params");
    return;
  }

  if (!date) {
    toast.error("Please select date");
    return;
  }

  try {
    setLoading(true);
    setRows([]);

    const result = await getRestaurantWiseTransactions({
      userid,
      date,
    });

    if (result.success) {
      setRows(result.data);
      toast.success(
        result.data.length
          ? "Restaurant wise transactions loaded"
          : "No data found"
      );
    } else {
      toast.error(result.message);
      setRows([]);
    }
  } catch (err) {
    toast.error("Something went wrong");
    setRows([]);
  } finally {
    setLoading(false);
  }
    };
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [actionLoadingKey, setActionLoadingKey] = useState("");

    const openConfirm = (row) => {
        setSelectedRow(row);
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        setConfirmOpen(false);
        setSelectedRow(null);
    };

    const updateSettlementTxn = async () => {
  if (!userid) {
    toast.error("User id missing");
    return;
  }

  if (
    !selectedRow?.order_id ||
    !selectedRow?.pk_order_id ||
    !selectedRow?.order_type
  ) {
    toast.error("Row data missing (order_id/pk_order_id/order_type)");
    return;
  }

  const key = `${selectedRow.pk_order_id}-${selectedRow.order_id}`;

  try {
    setActionLoadingKey(key);

    const result = await updateSettlementTransaction({
      userid,
      orderid: selectedRow.order_id,
      pk_orderid: selectedRow.pk_order_id,
      ordertype: selectedRow.order_type,
    });

    if (result.success) {
      toast.success(result.message);

      // optional refresh
      // fetchRestaurantWise();

      closeConfirm();
    } else {
      toast.error(result.message);
    }
  } finally {
    setActionLoadingKey("");
  }
};
  // Export Excel (exports ALL rows fetched, not just current page)
  const exportExcel = () => {
    if (!rows || rows.length === 0) {
      toast.error("No data to export.");
      return;
    }

    try {
      const filename = `AllRewiseTransactions_${name}_${date}.xlsx`;

      // Export clean JSON:
      // - Keep raw values
      // - Don’t include React badges etc.
      const exportRows = rows.map((r) => ({
  chaafoCommission: r.chaafoCommission || 'N/A',
  customer_mobile: r.customer_mobile || 'N/A',
  customer_name: r.customer_name || 'N/A',
  daily_order_number: r.daily_order_number || 'N/A',
  delivery_fee: r.delivery_fee || 'N/A',
  fk_user_id: r.fk_user_id || 'N/A',
  order_amount: r.order_amount || 'N/A',
  order_id: r.order_id || 'N/A',
  order_type: r.order_type || 'N/A',
  pay_amount: r.pay_amount || 'N/A',
  payment_link: r.payment_link || 'N/A',
  payment_status: r.payment_status || 'N/A',
  pk_order_id: r.pk_order_id || 'N/A',
  settelment_value: r.settelment_value || 'N/A',
  status: r.status || 'N/A',
  subtotal: r.subtotal || 'N/A',
  taxOnCommission: r.taxOnCommission || 'N/A',
  tax_cgst: r.tax_cgst || 'N/A',
  tax_sgst: r.tax_sgst || 'N/A',
}));

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AllSettlements");

      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        filename,
      );

      toast.success("Excel downloaded.");
    } catch {
      toast.error("Failed to export excel.");
    }
  };

    return (
        <div className="p-3 lg:p-5">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
                        <Building2 size={18} />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold">Restaurant Wise Transactions</h1>
                        <p className="text-sm text-gray-500">
                            {safe(name, "Client")} ({safe(userid, "—")})
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-2xl border bg-white p-3">
                <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-xl border px-3 py-2">
                        <div className="text-[11px] font-semibold text-gray-500">
                            User ID
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">
                            {safe(userid, "—")}
                        </div>
                    </div>

                    <label className="text-sm">
                        <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
                            <Calendar size={14} /> Date
                        </div>
                        <input
                            type="date"
                            className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </label>

                    <div className="flex items-end">
                        <button
                            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-50"
                            onClick={fetchRestaurantWise}
                            disabled={loading}
                            title="Fetch restaurant wise transactions"
                        >
                            <RefreshCcw size={16} />
                            {loading ? "Loading..." : "Fetch"}
                        </button>
                    </div>
            {/* Export button (next to filter) */}
            <div className="flex items-end md:col-span-1">
                <button
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-3 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
                onClick={exportExcel}
                disabled={loading || !rows || rows.length === 0}
                title={
                    !rows || rows.length === 0
                    ? "No data to export"
                    : "Export to Excel"
                }
                >
                <Download size={16} />
                Export Excel
                </button>
            </div>

                </div>
            </div>

            {/* Table */}
            <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
                <div className="max-h-[72vh] overflow-auto">
                    <table className="min-w-full table-auto text-left text-sm">
                        <thead className="sticky top-0 z-10 bg-[#01C37D] text-white">
                            <tr>
                                {[
                                    "#",
                                    "Order ID",
                                    "PK Order",
                                    "Daily #",
                                    "Customer",
                                    "Mobile",
                                    "Type",
                                    "Subtotal",
                                    "Order Amt",
                                    "Pay Amt",
                                    "SGST",
                                    "CGST",
                                    "Commission",
                                    "Tax on Comm.",
                                    "Settlement",
                                    "Delivery Fee",
                                    "Status",
                                    "Pay Status",
                                    "Action",
                                ].map((h) => (
                                    <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={`${r?.pk_order_id || idx}`} className="border-t">
                                    <td className="px-3 py-2">{idx + 1}</td>

                                    <td className="px-3 py-2 font-semibold">{safe(r?.order_id)}</td>
                                    <td className="px-3 py-2">{safe(r?.pk_order_id)}</td>
                                    <td className="px-3 py-2">{safe(r?.daily_order_number)}</td>

                                    <td className="px-3 py-2">{safe(r?.customer_name)}</td>
                                    <td className="px-3 py-2">{safe(r?.customer_mobile)}</td>

                                    <td className="px-3 py-2">{safe(r?.order_type)}</td>

                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.subtotal)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.order_amount)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.pay_amount)}</td>

                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.tax_sgst)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.tax_cgst)}</td>

                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.chaafoCommission)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.taxOnCommission)}</td>

                                    <td className="px-3 py-2 whitespace-nowrap font-semibold">
                                        ₹ {fmtMoney(r?.settelment_value)}
                                    </td>

                                    <td className="px-3 py-2 whitespace-nowrap">₹ {fmtMoney(r?.delivery_fee)}</td>

                                    <td className="px-3 py-2">{safe(r?.status)}</td>
                                    <td className="px-3 py-2">{safe(r?.payment_status)}</td>

                                    <td className="px-3 py-2">
                                        <button
                                            className="h-8 rounded-lg border px-3 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50"
                                            onClick={() => openConfirm(r)}
                                            disabled={actionLoadingKey === `${r?.pk_order_id}-${r?.order_id}`}
                                            title="Update settlement transaction"
                                        >
                                            {actionLoadingKey === `${r?.pk_order_id}-${r?.order_id}`
                                                ? "Updating..."
                                                : "Update"}
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={19} className="px-4 py-6 text-center text-gray-500">
                                        No data. Select date and click Fetch.
                                    </td>
                                </tr>
                            )}

                            {loading && (
                                <tr>
                                    <td colSpan={19} className="px-4 py-6 text-center text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
            </div>
            {confirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                        <div className="p-4 border-b">
                            <div className="text-lg font-semibold">Confirm Update</div>
                            <div className="text-xs text-gray-500 mt-1">
                                This will update settlement transaction for this order.
                            </div>
                        </div>

                        <div className="p-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Order ID</span>
                                <span className="font-semibold">{safe(selectedRow?.order_id, "—")}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">PK Order</span>
                                <span className="font-semibold">{safe(selectedRow?.pk_order_id, "—")}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Order Type</span>
                                <span className="font-semibold">{safe(selectedRow?.order_type, "—")}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Settlement</span>
                                <span className="font-semibold">
                                    ₹ {fmtMoney(selectedRow?.settelment_value)}
                                </span>
                            </div>
                        </div>

                        <div className="p-4 border-t flex gap-2">
                            <button
                                className="h-10 flex-1 rounded-xl border text-sm font-semibold hover:bg-gray-50"
                                onClick={closeConfirm}
                                disabled={!!actionLoadingKey}
                            >
                                Cancel
                            </button>
                            <button
                                className="h-10 flex-1 rounded-xl bg-[#01C37D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                                onClick={updateSettlementTxn}
                                disabled={!!actionLoadingKey}
                            >
                                {actionLoadingKey ? "Updating..." : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
